import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { bookingService } from '../services/bookingService';
import { exportService } from '../services/exportService';
import { teamService } from '../services/teamService';

const { FiCalendar, FiUsers, FiClock, FiTrendingUp, FiRefreshCw, FiWifi, FiWifiOff, FiActivity, FiUser, FiFilter, FiList, FiGrid, FiChevronDown, FiSearch, FiArrowRight, FiCheckCircle, FiXCircle, FiAlertCircle, FiCreditCard, FiDownload, FiFileText, FiFile, FiX, FiLogOut, FiEye, FiBarChart3, FiEdit3, FiRepeat } = FiIcons;

const TeamMemberDashboard = () => {
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [stats, setStats] = useState({
    totalAppointments: 0,
    todayAppointments: 0,
    pendingAppointments: 0,
    completedAppointments: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [connectionError, setConnectionError] = useState(null);

  // Filter states
  const [appointmentFilter, setAppointmentFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Export functionality states
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportFilter, setExportFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [exportMessage, setExportMessage] = useState('');

  // NEW: Reschedule states
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleMessage, setRescheduleMessage] = useState('');
  const [rescheduleData, setRescheduleData] = useState({
    selectedDate: '',
    selectedTimeSlot: '',
    reason: ''
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [rescheduleStep, setRescheduleStep] = useState(1);
  const [rescheduleSuggestions, setRescheduleSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Get current team member
  const currentUser = teamService.getCurrentUser();

  useEffect(() => {
    let mounted = true;

    const initializeData = async () => {
      if (!mounted) return;
      setIsLoading(true);
      await loadData();
      if (mounted) {
        setIsLoading(false);
        setupRealtimeSubscription();
      }
    };

    initializeData();

    return () => {
      mounted = false;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  // Filter appointments when any filter changes
  useEffect(() => {
    filterAppointments();
  }, [appointments, appointmentFilter, categoryFilter, statusFilter, searchTerm]);

  const filterAppointments = () => {
    if (!appointments || appointments.length === 0) {
      setFilteredAppointments([]);
      return;
    }

    const today = new Date();
    const todayString = today.toISOString().split('T')[0];

    // Calculate date ranges
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const startOfWeekString = startOfWeek.toISOString().split('T')[0];

    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (6 - today.getDay()));
    const endOfWeekString = endOfWeek.toISOString().split('T')[0];

    let filtered = [...appointments];

    // Date filter
    switch (appointmentFilter) {
      case 'today':
        filtered = filtered.filter(appointment =>
          appointment.appointment_date === todayString
        );
        break;
      case 'week':
        filtered = filtered.filter(appointment => {
          const appointmentDate = appointment.appointment_date;
          return appointmentDate >= startOfWeekString && appointmentDate <= endOfWeekString;
        });
        break;
      case 'upcoming':
        filtered = filtered.filter(appointment =>
          appointment.appointment_date >= todayString
        );
        break;
      case 'past':
        filtered = filtered.filter(appointment =>
          appointment.appointment_date < todayString
        );
        break;
      case 'all':
      default:
        // No date filtering
        break;
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(appointment =>
        appointment.appointment_type_name?.toLowerCase().includes(categoryFilter.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(appointment =>
        appointment.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(appointment =>
        appointment.patient_name?.toLowerCase().includes(searchLower) ||
        appointment.appointment_type_name?.toLowerCase().includes(searchLower) ||
        appointment.patient_email?.toLowerCase().includes(searchLower) ||
        appointment.booking_id?.toLowerCase().includes(searchLower)
      );
    }

    // Sort appointments: immediate appointments (today) first, then upcoming dates in chronological order
    filtered.sort((a, b) => {
      const dateA = new Date(`${a.appointment_date}T${a.appointment_time || '00:00'}`);
      const dateB = new Date(`${b.appointment_date}T${b.appointment_time || '00:00'}`);
      const todayDate = new Date(todayString);

      const isAToday = a.appointment_date === todayString;
      const isBToday = b.appointment_date === todayString;

      // If one is today and the other is not, today comes first
      if (isAToday && !isBToday) return -1;
      if (!isAToday && isBToday) return 1;

      // If both are today, sort by time (earliest first)
      if (isAToday && isBToday) {
        return dateA - dateB;
      }

      // For all other dates (not today), sort chronologically (earliest upcoming dates first)
      return dateA - dateB;
    });

    setFilteredAppointments(filtered);
  };

  const loadData = async () => {
    try {
      setConnectionError(null);

      // Test connection first
      const connectionTest = await bookingService.testConnection();
      if (connectionTest.success) {
        setIsConnected(true);

        // Load ALL appointments and stats using the bookingService
        const [appointmentsResult, statsResult] = await Promise.all([
          bookingService.getAllAppointments(),
          bookingService.getAppointmentStats()
        ]);

        if (appointmentsResult.success) {
          setAppointments(appointmentsResult.data || []);
        }

        if (statsResult.success) {
          setStats(statsResult.data || {
            totalAppointments: 0,
            todayAppointments: 0,
            pendingAppointments: 0,
            completedAppointments: 0
          });
        }

        setLastUpdated(new Date());
      } else {
        setIsConnected(false);
        setConnectionError('Unable to connect to appointment system');
      }
    } catch (error) {
      setIsConnected(false);
      setConnectionError('Connection temporarily unavailable');
    }
  };

  const setupRealtimeSubscription = () => {
    if (!isConnected) return;

    try {
      const newSubscription = bookingService.subscribeToChanges((payload) => {
        loadData(); // Reload data when changes occur
      });

      if (newSubscription) {
        setSubscription(newSubscription);
      }
    } catch (error) {
      console.error('Error setting up subscription:', error);
    }
  };

  const handleRefresh = async () => {
    await loadData();
  };

  const handleLogout = () => {
    teamService.logout();
    window.location.reload();
  };

  // NEW: Reschedule functions
  const handleRescheduleClick = async (appointment) => {
    setSelectedAppointment(appointment);
    setRescheduleMessage('');
    setRescheduleData({
      selectedDate: '',
      selectedTimeSlot: '',
      reason: ''
    });
    setRescheduleStep(1);
    setAvailableSlots([]);
    setShowRescheduleModal(true);

    // Load reschedule suggestions
    setLoadingSuggestions(true);
    try {
      const suggestionsResult = await bookingService.getReschedulesSuggestions(appointment.id, 7);
      if (suggestionsResult.success) {
        setRescheduleSuggestions(suggestionsResult.data.suggestions || []);
      }
    } catch (error) {
      console.error('Error loading suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleDateSelection = async (selectedDate) => {
    if (!selectedAppointment) return;

    setRescheduleData(prev => ({ ...prev, selectedDate }));
    setRescheduleMessage('Loading available time slots...');

    try {
      const slotsResult = await bookingService.getAvailableTimeSlotsForReschedule(
        selectedAppointment.appointment_type_id,
        selectedDate,
        selectedAppointment.id
      );

      if (slotsResult.success) {
        setAvailableSlots(slotsResult.data.availableSlots || []);
        if (slotsResult.data.availableSlots.length > 0) {
          setRescheduleStep(2);
          setRescheduleMessage('');
        } else {
          setRescheduleMessage('❌ No available time slots for this date. Please select a different date.');
        }
      } else {
        setRescheduleMessage(`❌ ${slotsResult.error}`);
      }
    } catch (error) {
      setRescheduleMessage(`❌ Error loading time slots: ${error.message}`);
    }
  };

  const handleTimeSlotSelection = (timeSlot) => {
    setRescheduleData(prev => ({ ...prev, selectedTimeSlot: timeSlot }));
  };

  const handleConfirmReschedule = async () => {
    if (!selectedAppointment || !rescheduleData.selectedDate || !rescheduleData.selectedTimeSlot) {
      setRescheduleMessage('❌ Please select both date and time slot');
      return;
    }

    setRescheduleLoading(true);
    setRescheduleMessage('');

    try {
      const result = await bookingService.rescheduleAppointment(
        selectedAppointment.id,
        rescheduleData.selectedDate,
        rescheduleData.selectedTimeSlot,
        rescheduleData.reason.trim() || null
      );

      if (result.success) {
        setRescheduleMessage('✅ ' + result.message);
        
        // Refresh appointments list
        await loadData();

        // Close modal after 3 seconds
        setTimeout(() => {
          setShowRescheduleModal(false);
          setSelectedAppointment(null);
          setRescheduleData({
            selectedDate: '',
            selectedTimeSlot: '',
            reason: ''
          });
          setRescheduleMessage('');
          setRescheduleStep(1);
        }, 3000);
      } else {
        setRescheduleMessage(`❌ ${result.error}`);
      }
    } catch (error) {
      setRescheduleMessage(`❌ Failed to reschedule: ${error.message}`);
    } finally {
      setRescheduleLoading(false);
    }
  };

  // Export functionality
  const handleExport = async (format) => {
    if (appointments.length === 0) {
      setExportMessage('No appointments available to export');
      return;
    }

    setExportLoading(true);
    setExportMessage('');

    try {
      let result;
      if (format === 'excel') {
        result = exportService.exportToExcel(appointments, exportFilter, customStartDate, customEndDate);
      } else if (format === 'pdf') {
        result = exportService.exportToPDF(appointments, exportFilter, customStartDate, customEndDate);
      }

      if (result.success) {
        setExportMessage(`✅ ${result.message} (${result.count} appointments)`);
        setTimeout(() => {
          setShowExportModal(false);
          setExportMessage('');
        }, 3000);
      } else {
        setExportMessage(`❌ ${result.message}`);
      }
    } catch (error) {
      setExportMessage(`❌ Export failed: ${error.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  const getFilteredExportCount = () => {
    const filtered = exportService.getFilteredAppointments(appointments, exportFilter, customStartDate, customEndDate);
    return filtered.length;
  };

  const getFilterCounts = () => {
    const today = new Date();
    const todayString = today.toISOString().split('T')[0];

    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const startOfWeekString = startOfWeek.toISOString().split('T')[0];

    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (6 - today.getDay()));
    const endOfWeekString = endOfWeek.toISOString().split('T')[0];

    return {
      all: appointments.length,
      today: appointments.filter(apt => apt.appointment_date === todayString).length,
      week: appointments.filter(apt => 
        apt.appointment_date >= startOfWeekString && apt.appointment_date <= endOfWeekString
      ).length,
      upcoming: appointments.filter(apt => apt.appointment_date >= todayString).length,
      past: appointments.filter(apt => apt.appointment_date < todayString).length,
      confirmed: appointments.filter(apt => apt.status?.toLowerCase() === 'confirmed').length,
      pending: appointments.filter(apt => apt.status?.toLowerCase() === 'pending').length,
      completed: appointments.filter(apt => apt.status?.toLowerCase() === 'completed').length,
      cancelled: appointments.filter(apt => apt.status?.toLowerCase() === 'cancelled').length
    };
  };

  const getUniqueCategories = () => {
    const categories = [...new Set(appointments.map(apt => apt.appointment_type_name).filter(Boolean))];
    return categories;
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'Not specified';
    try {
      const time = new Date(`1970-01-01T${timeString}`);
      return time.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return timeString;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // NEW: Format time slot for display
  const formatTimeSlot = (timeSlot) => {
    try {
      const [startTime, endTime] = timeSlot.split('-');
      return `${formatTime(startTime)} - ${formatTime(endTime)}`;
    } catch (error) {
      return timeSlot;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      case 'completed': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return FiCheckCircle;
      case 'pending': return FiAlertCircle;
      case 'cancelled': return FiXCircle;
      case 'completed': return FiCheckCircle;
      default: return FiClock;
    }
  };

  const getGenderIcon = (gender) => {
    if (!gender) return '👤';
    switch (gender.toLowerCase()) {
      case 'male':
      case 'm':
        return '👨';
      case 'female':
      case 'f':
        return '👩';
      default:
        return '👤';
    }
  };

  const isToday = (dateString) => {
    if (!dateString) return false;
    const today = new Date().toISOString().split('T')[0];
    return dateString === today;
  };

  const getFilterLabel = () => {
    switch (appointmentFilter) {
      case 'today': return "Today's Appointments";
      case 'week': return "This Week's Appointments";
      case 'upcoming': return 'Upcoming Appointments';
      case 'past': return 'Past Appointments';
      case 'all':
      default: return 'All Appointments';
    }
  };

  // NEW: Check if appointment can be rescheduled
  const canReschedule = (appointment) => {
    return appointment.status !== 'cancelled' && appointment.status !== 'completed';
  };

  const filterCounts = getFilterCounts();
  const categories = getUniqueCategories();

  const statCards = [
    { title: 'Total Patients', value: stats.totalAppointments, icon: FiUsers, color: 'from-blue-600 to-cyan-600', change: '+12%' },
    { title: "Today's Appointments", value: stats.todayAppointments, icon: FiCalendar, color: 'from-green-600 to-emerald-600', change: '+8%' },
    { title: 'Pending Reviews', value: stats.pendingAppointments, icon: FiClock, color: 'from-yellow-600 to-orange-600', change: '-3%' },
    { title: 'Completed Today', value: stats.completedAppointments, icon: FiTrendingUp, color: 'from-purple-600 to-pink-600', change: '+15%' }
  ];

  const renderListView = () => (
    <div className="space-y-3">
      {filteredAppointments.map((appointment, index) => (
        <motion.div
          key={appointment.id || index}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: index * 0.05 }}
          className={`p-4 rounded-lg border transition-all duration-200 hover:shadow-md ${
            isToday(appointment.appointment_date) 
              ? 'bg-gradient-to-r from-blue-50 to-green-50 border-blue-200 ring-1 ring-blue-200' 
              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Number Badge */}
              <div className="flex-shrink-0">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  isToday(appointment.appointment_date) 
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' 
                    : 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
                }`}>
                  {index + 1}
                </div>
              </div>

              {/* Patient Avatar with Gender Icon */}
              <div className={`h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 text-lg ${
                isToday(appointment.appointment_date) 
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg' 
                  : 'bg-gradient-to-r from-blue-600 to-cyan-600'
              }`}>
                <span className="text-white">
                  {getGenderIcon(appointment.patient_gender)}
                </span>
              </div>

              {/* Patient Information */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="font-bold text-gray-900 text-lg truncate">
                    {appointment.patient_name || 'Patient Name'}
                  </h4>
                  {isToday(appointment.appointment_date) && (
                    <span className="px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-full shadow-sm">
                      Today
                    </span>
                  )}
                  {/* NEW: Reschedule indicator */}
                  {appointment.reschedule_count > 0 && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full border border-purple-200">
                      <SafeIcon icon={FiRepeat} className="h-3 w-3 mr-1 inline" />
                      Rescheduled {appointment.reschedule_count}x
                    </span>
                  )}
                </div>

                {/* Essential Details */}
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiCalendar} className="h-4 w-4 text-blue-500" />
                    <span className="font-medium">{formatDate(appointment.appointment_date)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiClock} className="h-4 w-4 text-green-500" />
                    <span className="font-medium">{formatTime(appointment.appointment_time)}</span>
                  </div>
                  {appointment.appointment_type_name && (
                    <div className="flex items-center space-x-1">
                      <SafeIcon icon={FiUser} className="h-4 w-4 text-purple-500" />
                      <span className="font-medium truncate max-w-32">{appointment.appointment_type_name}</span>
                    </div>
                  )}
                  {appointment.patient_gender && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-medium">
                      {appointment.patient_gender}
                    </span>
                  )}
                  {appointment.consultation_fee && (
                    <div className="flex items-center space-x-1">
                      <SafeIcon icon={FiCreditCard} className="h-4 w-4 text-green-600" />
                      <span className="font-medium text-green-600">₹{appointment.consultation_fee}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Status and Actions */}
            <div className="flex flex-col items-end space-y-2">
              <div className="flex items-center space-x-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(appointment.status)}`}>
                  <SafeIcon icon={getStatusIcon(appointment.status)} className="h-3 w-3 mr-1 inline" />
                  {appointment.status || 'Pending'}
                </span>

                {/* NEW: Reschedule Button for Team Members */}
                {canReschedule(appointment) && (
                  <button
                    onClick={() => handleRescheduleClick(appointment)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Reschedule appointment"
                  >
                    <SafeIcon icon={FiEdit3} className="h-4 w-4" />
                  </button>
                )}
              </div>

              {appointment.booking_id && (
                <span className="text-xs text-gray-400">
                  #{appointment.booking_id}
                </span>
              )}

              {/* NEW: Original appointment info for rescheduled appointments */}
              {appointment.original_appointment_date && (
                <div className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded max-w-48 truncate">
                  Originally: {formatDate(appointment.original_appointment_date)} {formatTime(appointment.original_appointment_time)}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );

  const renderGridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {filteredAppointments.map((appointment, index) => (
        <motion.div
          key={appointment.id || index}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: index * 0.05 }}
          className={`p-4 rounded-lg border transition-all duration-200 hover:shadow-lg ${
            isToday(appointment.appointment_date) 
              ? 'bg-gradient-to-br from-blue-50 to-green-50 border-blue-200 ring-1 ring-blue-200' 
              : 'bg-white border-gray-200 hover:border-gray-300'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${
                isToday(appointment.appointment_date) 
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' 
                  : 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
              }`}>
                {index + 1}
              </div>
              <span className="text-lg">
                {getGenderIcon(appointment.patient_gender)}
              </span>
              {isToday(appointment.appointment_date) && (
                <span className="px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                  Today
                </span>
              )}
              {/* NEW: Reschedule indicator */}
              {appointment.reschedule_count > 0 && (
                <span className="px-1 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                  <SafeIcon icon={FiRepeat} className="h-3 w-3" />
                </span>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(appointment.status)}`}>
                <SafeIcon icon={getStatusIcon(appointment.status)} className="h-3 w-3 mr-1 inline" />
                {appointment.status || 'Pending'}
              </span>

              {/* NEW: Reschedule Button */}
              {canReschedule(appointment) && (
                <button
                  onClick={() => handleRescheduleClick(appointment)}
                  className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="Reschedule appointment"
                >
                  <SafeIcon icon={FiEdit3} className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          {/* Patient Name */}
          <h4 className="font-bold text-gray-900 text-lg mb-3 truncate">
            {appointment.patient_name || 'Patient Name'}
          </h4>

          {/* Details */}
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiCalendar} className="h-4 w-4 text-blue-500" />
              <span>{formatDate(appointment.appointment_date)}</span>
            </div>
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiClock} className="h-4 w-4 text-green-500" />
              <span>{formatTime(appointment.appointment_time)}</span>
            </div>
            {appointment.appointment_type_name && (
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiUser} className="h-4 w-4 text-purple-500" />
                <span className="truncate">{appointment.appointment_type_name}</span>
              </div>
            )}
            {appointment.consultation_fee && (
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiCreditCard} className="h-4 w-4 text-green-600" />
                <span className="font-medium text-green-600">₹{appointment.consultation_fee}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
              {appointment.patient_gender || 'N/A'}
            </span>
            {appointment.booking_id && (
              <span className="text-xs text-gray-400">
                #{appointment.booking_id}
              </span>
            )}
          </div>

          {/* NEW: Original appointment info for rescheduled appointments */}
          {appointment.original_appointment_date && (
            <div className="mt-2 text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded truncate">
              Originally: {formatDate(appointment.original_appointment_date)}
            </div>
          )}
        </motion.div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="h-10 w-10 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center mr-4">
                <SafeIcon icon={FiActivity} className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Best Podiatrist Dashboard</h1>
                <p className="text-gray-600">Team Member: {currentUser?.fullName}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className={`flex items-center px-3 py-1 rounded-full text-sm ${
                isConnected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                <SafeIcon icon={isConnected ? FiWifi : FiWifiOff} className="h-4 w-4 mr-1" />
                {isConnected ? 'Online' : 'Offline'}
              </div>

              <motion.button
                onClick={handleRefresh}
                disabled={isLoading}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                title="Refresh data"
              >
                <SafeIcon icon={FiRefreshCw} className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
              </motion.button>

              <motion.button
                onClick={handleLogout}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <SafeIcon icon={FiLogOut} className="h-4 w-4" />
                <span className="text-sm">Logout</span>
              </motion.button>
            </div>
          </div>

          {lastUpdated && (
            <p className="text-sm text-gray-500 mt-2">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}

          {connectionError && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                <strong>Connection Issue:</strong> {connectionError}
              </p>
              <button
                onClick={handleRefresh}
                className="text-sm text-red-600 hover:text-red-800 underline mt-1"
              >
                Try reconnecting
              </button>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((card, index) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6 border border-gray-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    {isLoading ? (
                      <div className="animate-pulse bg-gray-200 h-8 w-16 rounded"></div>
                    ) : (
                      card.value
                    )}
                  </p>
                  <p className="text-sm text-green-600 mt-1">{card.change} from last week</p>
                </div>
                <div className={`p-3 rounded-lg bg-gradient-to-r ${card.color}`}>
                  <SafeIcon icon={card.icon} className="h-6 w-6 text-white" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Appointments List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg border border-gray-100"
        >
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Patient Appointment Bookings</h2>
              
              <div className="flex items-center space-x-4">
                {/* Export Button */}
                <motion.button
                  onClick={() => setShowExportModal(true)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-md"
                  disabled={appointments.length === 0}
                >
                  <SafeIcon icon={FiDownload} className="h-4 w-4" />
                  <span className="text-sm font-medium">Export</span>
                </motion.button>

                {/* View Mode Toggle */}
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-all duration-200 ${
                      viewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`}
                    title="List View"
                  >
                    <SafeIcon icon={FiList} className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md transition-all duration-200 ${
                      viewMode === 'grid' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`}
                    title="Grid View"
                  >
                    <SafeIcon icon={FiGrid} className="h-4 w-4" />
                  </button>
                </div>

                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <span>{filteredAppointments.length} appointments</span>
                  <div className="h-2 w-2 bg-green-400 rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by patient name, appointment type, email, or booking ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filter Section */}
            <div className="space-y-4">
              {/* Main Filters */}
              <div className="flex flex-wrap items-center gap-2">
                <SafeIcon icon={FiFilter} className="h-4 w-4 text-gray-500" />

                {/* Date Filters */}
                <div className="flex flex-wrap gap-1">
                  {[
                    { key: 'all', label: 'All', count: filterCounts.all },
                    { key: 'today', label: 'Today', count: filterCounts.today },
                    { key: 'week', label: 'This Week', count: filterCounts.week },
                    { key: 'upcoming', label: 'Upcoming', count: filterCounts.upcoming },
                    { key: 'past', label: 'Past', count: filterCounts.past }
                  ].map(filter => (
                    <button
                      key={filter.key}
                      onClick={() => setAppointmentFilter(filter.key)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                        appointmentFilter === filter.key 
                          ? 'bg-blue-600 text-white shadow-sm' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {filter.label} ({filter.count})
                    </button>
                  ))}
                </div>

                {/* Advanced Filters Toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center space-x-1 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors"
                >
                  <span className="text-sm">More Filters</span>
                  <SafeIcon icon={FiChevronDown} className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>
              </div>

              {/* Advanced Filters */}
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-3 pt-4 border-t border-gray-200"
                >
                  {/* Status Filters */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <div className="flex flex-wrap gap-1">
                      {[
                        { key: 'all', label: 'All Status', count: filterCounts.all },
                        { key: 'confirmed', label: 'Confirmed', count: filterCounts.confirmed },
                        { key: 'pending', label: 'Pending', count: filterCounts.pending },
                        { key: 'completed', label: 'Completed', count: filterCounts.completed },
                        { key: 'cancelled', label: 'Cancelled', count: filterCounts.cancelled }
                      ].map(status => (
                        <button
                          key={status.key}
                          onClick={() => setStatusFilter(status.key)}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                            statusFilter === status.key 
                              ? 'bg-green-600 text-white shadow-sm' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {status.label} ({status.count})
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Category Filters */}
                  {categories.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Appointment Type</label>
                      <div className="flex flex-wrap gap-1">
                        <button
                          onClick={() => setCategoryFilter('all')}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                            categoryFilter === 'all' 
                              ? 'bg-purple-600 text-white shadow-sm' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          All Types ({appointments.length})
                        </button>
                        {categories.map(category => (
                          <button
                            key={category}
                            onClick={() => setCategoryFilter(category)}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                              categoryFilter === category 
                                ? 'bg-purple-600 text-white shadow-sm' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {category} ({appointments.filter(apt => apt.appointment_type_name === category).length})
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </div>

          <div className="p-6">
            {!isConnected ? (
              <div className="text-center py-12">
                <SafeIcon icon={FiWifiOff} className="h-12 w-12 text-red-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Connection Issue</h3>
                <p className="text-gray-500 mb-4">Unable to connect to the appointment system.</p>
                <button
                  onClick={handleRefresh}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Retry Connection
                </button>
              </div>
            ) : isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                      <div className="bg-gray-200 h-12 w-12 rounded-full flex-shrink-0"></div>
                      <div className="flex-1 space-y-2">
                        <div className="bg-gray-200 h-4 w-3/4 rounded"></div>
                        <div className="bg-gray-200 h-3 w-1/2 rounded"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredAppointments.length === 0 ? (
              <div className="text-center py-12">
                <SafeIcon icon={FiCalendar} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No appointments found</h3>
                <p className="text-gray-500">
                  {searchTerm 
                    ? `No appointments match "${searchTerm}"` 
                    : appointmentFilter === 'today' 
                      ? "No appointments scheduled for today." 
                      : appointmentFilter === 'week' 
                        ? "No appointments scheduled for this week." 
                        : "No patient appointments found with current filters."
                  }
                </p>
                {(searchTerm || appointmentFilter !== 'all' || categoryFilter !== 'all' || statusFilter !== 'all') && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setAppointmentFilter('all');
                      setCategoryFilter('all');
                      setStatusFilter('all');
                    }}
                    className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-md font-medium text-gray-700">{getFilterLabel()}</h3>
                  <div className="flex items-center space-x-3 text-sm text-gray-500">
                    <span>
                      Showing {filteredAppointments.length} of {appointments.length} appointments (Today First, then Upcoming)
                    </span>
                    {searchTerm && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                        Search: "{searchTerm}"
                      </span>
                    )}
                  </div>
                </div>

                {/* Render based on view mode */}
                {viewMode === 'list' ? renderListView() : renderGridView()}
              </div>
            )}
          </div>
        </motion.div>

        {/* NEW: Reschedule Modal - Same as in Overview.jsx but simplified for team members */}
        <AnimatePresence>
          {showRescheduleModal && selectedAppointment && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowRescheduleModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiEdit3} className="h-5 w-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Reschedule Appointment</h3>
                  </div>
                  <button
                    onClick={() => setShowRescheduleModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    disabled={rescheduleLoading}
                  >
                    <SafeIcon icon={FiX} className="h-5 w-5 text-gray-500" />
                  </button>
                </div>

                {/* Current Appointment Details */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-gray-900 mb-2">Current Appointment</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                    <div>
                      <strong>Patient:</strong> {selectedAppointment.patient_name}
                    </div>
                    <div>
                      <strong>Date:</strong> {formatDate(selectedAppointment.appointment_date)}
                    </div>
                    <div>
                      <strong>Time:</strong> {formatTime(selectedAppointment.appointment_time)}
                    </div>
                    <div>
                      <strong>Type:</strong> {selectedAppointment.appointment_type_name}
                    </div>
                  </div>
                </div>

                {rescheduleStep === 1 ? (
                  // Step 1: Date Selection
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Select New Date</h4>
                      
                      {/* Quick Suggestions */}
                      {loadingSuggestions ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="flex items-center space-x-2">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            <span className="text-gray-600">Loading suggestions...</span>
                          </div>
                        </div>
                      ) : rescheduleSuggestions.length > 0 ? (
                        <div className="mb-6">
                          <h5 className="text-md font-medium text-gray-700 mb-3">Suggested Available Dates</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {rescheduleSuggestions.slice(0, 6).map((suggestion, index) => (
                              <motion.button
                                key={suggestion.date}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                onClick={() => handleDateSelection(suggestion.date)}
                                className="p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all text-left"
                              >
                                <div className="font-medium text-gray-900">{suggestion.displayDate}</div>
                                <div className="text-sm text-gray-600">{suggestion.dayName}</div>
                                <div className="text-xs text-green-600 mt-1">
                                  {suggestion.totalSlots} slots available
                                </div>
                              </motion.button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-sm text-yellow-700">
                            No quick suggestions available. Please select a custom date below.
                          </p>
                        </div>
                      )}

                      {/* Custom Date Picker */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Or choose a custom date
                        </label>
                        <input
                          type="date"
                          value={rescheduleData.selectedDate}
                          onChange={(e) => setRescheduleData(prev => ({ ...prev, selectedDate: e.target.value }))}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {rescheduleData.selectedDate && (
                          <button
                            onClick={() => handleDateSelection(rescheduleData.selectedDate)}
                            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Check Availability for {formatDate(rescheduleData.selectedDate)}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Reschedule Message */}
                    {rescheduleMessage && (
                      <div className={`p-3 rounded-lg ${
                        rescheduleMessage.startsWith('✅') 
                          ? 'bg-green-50 border border-green-200 text-green-700' 
                          : rescheduleMessage.includes('Loading') 
                            ? 'bg-blue-50 border border-blue-200 text-blue-700'
                            : 'bg-red-50 border border-red-200 text-red-700'
                      }`}>
                        <p className="text-sm">{rescheduleMessage}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  // Step 2: Time Slot Selection
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-gray-900">
                        Select Time Slot for {formatDate(rescheduleData.selectedDate)}
                      </h4>
                      <button
                        onClick={() => setRescheduleStep(1)}
                        className="text-sm text-blue-600 hover:text-blue-700 underline"
                      >
                        ← Choose Different Date
                      </button>
                    </div>

                    {availableSlots.length > 0 ? (
                      <div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
                          {availableSlots.map((slot, index) => (
                            <motion.button
                              key={slot}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: index * 0.05 }}
                              onClick={() => handleTimeSlotSelection(slot)}
                              className={`p-3 border rounded-lg text-sm font-medium transition-all ${
                                rescheduleData.selectedTimeSlot === slot
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {formatTimeSlot(slot)}
                            </motion.button>
                          ))}
                        </div>

                        {/* Reason for Reschedule */}
                        <div className="mb-6">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reason for Reschedule (Optional)
                          </label>
                          <textarea
                            value={rescheduleData.reason}
                            onChange={(e) => setRescheduleData(prev => ({ ...prev, reason: e.target.value }))}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter reason for rescheduling..."
                            disabled={rescheduleLoading}
                          />
                        </div>

                        {/* Confirmation */}
                        {rescheduleData.selectedTimeSlot && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                            <h5 className="font-medium text-green-800 mb-2">Reschedule Summary</h5>
                            <div className="text-sm text-green-700 space-y-1">
                              <p><strong>New Date:</strong> {formatDate(rescheduleData.selectedDate)}</p>
                              <p><strong>New Time:</strong> {formatTimeSlot(rescheduleData.selectedTimeSlot)}</p>
                              <p><strong>Patient:</strong> {selectedAppointment.patient_name}</p>
                              <p><strong>Appointment Type:</strong> {selectedAppointment.appointment_type_name}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <SafeIcon icon={FiClock} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h5 className="text-lg font-medium text-gray-900 mb-2">No Available Slots</h5>
                        <p className="text-gray-600">No time slots are available for the selected date.</p>
                      </div>
                    )}

                    {/* Reschedule Message */}
                    {rescheduleMessage && (
                      <div className={`p-3 rounded-lg ${
                        rescheduleMessage.startsWith('✅') 
                          ? 'bg-green-50 border border-green-200 text-green-700' 
                          : 'bg-red-50 border border-red-200 text-red-700'
                      }`}>
                        <p className="text-sm">{rescheduleMessage}</p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    {availableSlots.length > 0 && (
                      <div className="flex space-x-3">
                        <button
                          onClick={() => setShowRescheduleModal(false)}
                          disabled={rescheduleLoading}
                          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleConfirmReschedule}
                          disabled={rescheduleLoading || !rescheduleData.selectedTimeSlot}
                          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                        >
                          {rescheduleLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>Rescheduling...</span>
                            </>
                          ) : (
                            <>
                              <SafeIcon icon={FiCheckCircle} className="h-4 w-4" />
                              <span>Confirm Reschedule</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Export Modal */}
        <AnimatePresence>
          {showExportModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowExportModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Export Appointments</h3>
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiX} className="h-5 w-5 text-gray-500" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Date Filter Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date Range
                    </label>
                    <select
                      value={exportFilter}
                      onChange={(e) => setExportFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Appointments</option>
                      <option value="today">Today Only</option>
                      <option value="week">This Week</option>
                      <option value="custom">Custom Date Range</option>
                    </select>
                  </div>

                  {/* Custom Date Range */}
                  {exportFilter === 'custom' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}

                  {/* Export Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                      <strong>Selected Range:</strong> {exportService.getDateRangeDisplayText(exportFilter, customStartDate, customEndDate)}
                      <br />
                      <strong>Appointments to Export:</strong> {getFilteredExportCount()}
                    </p>
                  </div>

                  {/* Export Message */}
                  {exportMessage && (
                    <div className={`p-3 rounded-lg ${
                      exportMessage.startsWith('✅') 
                        ? 'bg-green-50 border border-green-200 text-green-700' 
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                      <p className="text-sm">{exportMessage}</p>
                    </div>
                  )}

                  {/* Export Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleExport('excel')}
                      disabled={exportLoading || getFilteredExportCount() === 0}
                      className="flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <SafeIcon icon={FiFileText} className="h-4 w-4" />
                      <span className="font-medium">
                        {exportLoading ? 'Exporting...' : 'Excel'}
                      </span>
                    </button>
                    <button
                      onClick={() => handleExport('pdf')}
                      disabled={exportLoading || getFilteredExportCount() === 0}
                      className="flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <SafeIcon icon={FiFile} className="h-4 w-4" />
                      <span className="font-medium">
                        {exportLoading ? 'Exporting...' : 'PDF'}
                      </span>
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Copyright Footer */}
        <div className="mt-8 text-center text-sm text-gray-500 border-t border-gray-200 pt-4">
          <p>Best Podiatrist Copyright © 2025. Dr. Amit Kumar C Jain (Diabetic Foot Surgeon). All Rights Reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberDashboard;